import React, { useState, useEffect, useRef } from 'react';
import { NotificationIcon } from './ui/Icons';
import type { DailyAlertNotification } from '../types/daily-alert';
import { formatUtcDateKey } from '../utils/daily-user-budget';

interface NotificationDropdownProps {
  notifications: DailyAlertNotification[];
  notificationThreshold: number;
  onDismissNotification: (notificationId: string) => void;
  onDismissAll: () => void;
  monitorError?: string | null;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  notificationThreshold,
  onDismissNotification,
  onDismissAll,
  monitorError
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [today, setToday] = useState(() => formatUtcDateKey(new Date()));
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications]);

  useEffect(() => {
    let timeoutId: number;
    const scheduleUtcRollover = () => {
      const now = new Date();
      const nextUtcDay = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1
      );
      timeoutId = window.setTimeout(() => {
        setToday(formatUtcDateKey(new Date()));
        scheduleUtcRollover();
      }, Math.max(1, nextUtcDay - now.getTime()));
    };
    scheduleUtcRollover();
    return () => window.clearTimeout(timeoutId);
  }, []);

  const activeNotifications = notifications
    .filter(n => !n.dismissed && n.date === today)
    .sort((a, b) => b.cost - a.cost); // 料金の高い順にソート

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 transform hover:scale-105"
        title="Notifications"
      >
        <NotificationIcon className="w-6 h-6" />
        {(activeNotifications.length > 0 || monitorError) && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {monitorError ? '!' : activeNotifications.length}
          </span>
        )}
      </button>
      
      {/* Notification Dropdown */}
      {showNotifications && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  High Usage Alerts
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Users at or above ${notificationThreshold} per UTC day
                </p>
              </div>
              {activeNotifications.length > 0 && (
                <button
                  onClick={onDismissAll}
                  className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
          {monitorError && (
            <div className="p-3 border-b border-red-200 bg-red-50 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
              {monitorError}
            </div>
          )}
          <div className="max-h-96 overflow-y-auto custom-scrollbar">
            {activeNotifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No active alerts
              </div>
            ) : (
              activeNotifications.map(notification => {
                const isToday = notification.date === today;
                
                return (
                  <div key={notification.id} className="p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${isToday ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                          {notification.apiKeyName}
                        </p>
                        <p className={`text-sm mt-1 ${isToday ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                          ${notification.cost.toFixed(2)} {notification.alertContext ? `- ${notification.alertContext}` : 'spent today'}
                        </p>
                        {notification.costQuality === 'calculated' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            Calculated allocation
                          </span>
                        )}
                        {notification.costQuality === 'estimated' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                            Estimated allocation
                          </span>
                        )}
                        {notification.alertType && notification.alertType !== 'actual' && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                            notification.alertType === 'estimated' 
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}>
                            {notification.alertType === 'estimated' ? '📊 Projected' : '📈 Average'}
                          </span>
                        )}
                        {notification.sources && notification.sources.length > 1 && (
                          <p className="text-xs mt-1 text-gray-500 dark:text-gray-500">
                            {notification.sources.length} source keys across {new Set(notification.sources.map(source => source.provider)).size} provider(s)
                          </p>
                        )}
                        <p className={`text-xs mt-1 ${isToday ? 'text-gray-500 dark:text-gray-500' : 'text-gray-400 dark:text-gray-600'}`}>
                          {notification.date}
                        </p>
                      </div>
                      <button
                        onClick={() => onDismissNotification(notification.id)}
                        className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
